/**
 * ExceptionPanel — collapsible CRUD panel for guardrail exceptions.
 *
 * Embedded within each guardrail card on the Guardrails page.
 * Displays exception type metadata, table of records, and create/edit/delete actions.
 *
 * Requirements: 7.2, 7.3, 7.4, 8.1, 9.1, 10.1, 10.2
 */

import { useState, useEffect, useCallback } from 'react';
import {
  LdsBox,
  LdsTypography,
  LdsFormField,
  LdsTextField,
  LdsButton,
  LdsIconButton,
  LdsTable,
  LdsAlert,
  LdsProgressCircular,
  LdsChip,
  LdsDialog,
} from '@lds/react';
import { LdsIconPencil, LdsIconTrash, LdsIconPlus } from '@lds/react-icons';
import { AxiosInstance } from 'axios';
import { GuardrailPanel } from '../guardrails/GuardrailPanel';
import styles from './ExceptionPanel.module.scss';

export type Role = 'admin' | 'viewer';

export interface ExceptionType {
  key: string;
  label: string;
  pattern: 'exact' | 'starts_with' | 'contains';
  placeholder: string;
}

export interface ExceptionRecord {
  accessKeyId: string;
  date: string;
}

export interface ExceptionView extends ExceptionRecord {
  status: 'active' | 'expired';
}

export interface ExceptionPanelProps {
  guardrail: string;
  role: Role;
  apiClient: AxiosInstance;
  exceptionTypes: ExceptionType[];
}

function classifyStatus(dateStr: string): 'active' | 'expired' {
  const parts = dateStr.split('/');
  if (parts.length !== 3) return 'expired';
  const [day, month, year] = parts.map(Number);
  const expirationDate = new Date(year, month - 1, day);
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  return expirationDate > today ? 'active' : 'expired';
}

export function ExceptionPanel({ guardrail, role, apiClient, exceptionTypes }: ExceptionPanelProps) {
  const [expanded, setExpanded] = useState(false);
  const [exceptions, setExceptions] = useState<ExceptionView[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');

  // Form state
  const [formOpen, setFormOpen] = useState(false);
  const [formMode, setFormMode] = useState<'create' | 'edit'>('create');
  const [formAccessKeyId, setFormAccessKeyId] = useState('');
  const [formDate, setFormDate] = useState('');
  const [formError, setFormError] = useState<string | null>(null);

  // Delete confirmation
  const [deleteDialog, setDeleteDialog] = useState<{ open: boolean; accessKeyId: string }>({
    open: false,
    accessKeyId: '',
  });

  const fetchExceptions = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await apiClient.get<{ exceptions: any[] }>(
        `/exceptions?guardrail=${guardrail}`
      );
      const records = response.data.exceptions ?? [];
      const withStatus: ExceptionView[] = records.map((rec: any) => ({
        accessKeyId: rec.accessKeyId ?? rec.access_key_id ?? '',
        date: rec.date ?? '',
        status: classifyStatus(rec.date ?? ''),
      }));
      setExceptions(withStatus);
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Falha ao carregar exceções';
      setError(message);
    } finally {
      setLoading(false);
    }
  }, [apiClient, guardrail]);

  useEffect(() => {
    if (expanded) {
      fetchExceptions();
    }
  }, [expanded, fetchExceptions]);

  const activeCount = exceptions.filter((e) => e.status === 'active').length;

  const filteredExceptions = exceptions.filter((exc) => {
    if (!searchTerm) return true;
    return (exc.accessKeyId ?? '').toLowerCase().includes(searchTerm.toLowerCase());
  });

  const openCreateForm = () => {
    setFormMode('create');
    setFormAccessKeyId('');
    setFormDate('');
    setFormError(null);
    setFormOpen(true);
  };

  const openEditForm = (exc: ExceptionView) => {
    setFormMode('edit');
    setFormAccessKeyId(exc.accessKeyId);
    setFormDate(exc.date);
    setFormError(null);
    setFormOpen(true);
  };

  const handleFormSubmit = async () => {
    setFormError(null);

    // Validar que a data é futura
    const parts = formDate.split('/');
    if (parts.length !== 3) {
      setFormError('Data inválida. Use o formato DD/MM/YYYY.');
      return;
    }
    const [day, month, year] = parts.map(Number);
    if (!day || !month || !year || day > 31 || month > 12) {
      setFormError('Data inválida. Use o formato DD/MM/YYYY.');
      return;
    }
    const inputDate = new Date(year, month - 1, day);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    if (inputDate <= today) {
      setFormError('A data de validade deve ser uma data futura.');
      return;
    }
    const maxDate = new Date(today);
    maxDate.setDate(today.getDate() + 14);
    if (inputDate > maxDate) {
      setFormError('O prazo máximo de prorrogação é de 14 dias.');
      return;
    }

    try {
      if (formMode === 'create') {
        await apiClient.post('/exceptions', {
          access_key_id: formAccessKeyId,
          date: formDate,
        });
      } else {
        await apiClient.put(`/exceptions/${formAccessKeyId}`, { date: formDate });
      }
      setFormOpen(false);
      await fetchExceptions();
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Operação falhou';
      setFormError(message);
    }
  };

  const handleDeleteConfirm = async () => {
    try {
      await apiClient.delete(`/exceptions/${deleteDialog.accessKeyId}`);
      setDeleteDialog({ open: false, accessKeyId: '' });
      await fetchExceptions();
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Falha ao remover';
      setError(message);
      setDeleteDialog({ open: false, accessKeyId: '' });
    }
  };

  const primaryExceptionType = exceptionTypes[0];

  return (
    <div data-testid={`exception-panel-${guardrail}`}>
      <GuardrailPanel title="Exceções" expanded={expanded} onChange={setExpanded} summary={
        <LdsChip label={`${activeCount} ativa${activeCount !== 1 ? 's' : ''}`} size="sm"
          color={activeCount > 0 ? 'info' : 'neutral'} />
      }>

      {/* Expanded content */}
      {expanded && (
        <div className={styles.panelContent} id={`exception-content-${guardrail}`}>
          {/* Error */}
          {error && <LdsAlert color="critical" description={error} />}

          {/* Loading */}
          {loading && (
            <LdsBox className={styles.centered}>
              <LdsProgressCircular indeterminate animated ariaLabel="Carregando exceções" />
            </LdsBox>
          )}

          {/* Content */}
          {!loading && (
            <>
              {/* Search + Create button */}
              <div className={styles.toolbar}>
                <div className={styles.searchInline}>
                  <LdsFormField label={`Buscar por ${primaryExceptionType?.label ?? 'ID'}`} fieldId={`exc-search-${guardrail}`}>
                    <LdsTextField
                      value={searchTerm}
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) => setSearchTerm(e.target.value)}
                      aria-label="Buscar exceções"
                      data-field={`exc-search-${guardrail}`}
                    />
                  </LdsFormField>
                </div>
                {role === 'admin' && (
                  <LdsButton
                    variant="contained"
                    size="sm"
                    onClick={openCreateForm}
                    aria-label="Nova exceção"
                    data-action={`create-exception-${guardrail}`}
                  >
                    <span className={styles.btnContent}>
                      <LdsIconPlus size="14" />
                      Nova Exceção
                    </span>
                  </LdsButton>
                )}
              </div>

              {/* Table */}
              {filteredExceptions.length > 0 ? (
                <LdsTable aria-label={`Exceções de ${guardrail}`} data-testid={`exceptions-table-${guardrail}`} striped>
                  <thead>
                    <tr>
                      <th>{primaryExceptionType?.label ?? 'ID'}</th>
                      <th>Validade (DD/MM/YYYY)</th>
                      <th>Status</th>
                      {role === 'admin' && <th>Ações</th>}
                    </tr>
                  </thead>
                  <tbody>
                    {filteredExceptions.map((exc) => (
                      <tr key={exc.accessKeyId}>
                        <td>{exc.accessKeyId}</td>
                        <td>{exc.date}</td>
                        <td>
                          <LdsChip
                            label={exc.status === 'active' ? 'Vigente' : 'Expirada'}
                            color={exc.status === 'active' ? 'success' : 'neutral'}
                            size="sm"
                          />
                        </td>
                        {role === 'admin' && (
                          <td>
                            <div className={styles.actions}>
                              <LdsIconButton
                                size="sm"
                                onClick={() => openEditForm(exc)}
                                aria-label={`Editar ${exc.accessKeyId}`}
                              >
                                <LdsIconPencil size="16" />
                              </LdsIconButton>
                              <LdsIconButton
                                size="sm"
                                color="critical"
                                onClick={() => setDeleteDialog({ open: true, accessKeyId: exc.accessKeyId })}
                                aria-label={`Remover ${exc.accessKeyId}`}
                              >
                                <LdsIconTrash size="16" />
                              </LdsIconButton>
                            </div>
                          </td>
                        )}
                      </tr>
                    ))}
                  </tbody>
                </LdsTable>
              ) : (
                <LdsAlert color="info" description="Nenhuma exceção cadastrada para este guardrail." />
              )}
            </>
          )}
        </div>
      )}

      </GuardrailPanel>

      {/* Create/Edit Dialog */}
      {formOpen && (
        <LdsDialog
          open={formOpen}
          onClose={() => setFormOpen(false)}
          title={formMode === 'create' ? 'Nova Exceção' : 'Editar Exceção'}
          closeButtonAriaLabel="Fechar"
          data-testid={`exception-form-${guardrail}`}
          footer={
            <>
              <LdsButton variant="outlined" onClick={() => setFormOpen(false)}>
                Cancelar
              </LdsButton>
              <LdsButton variant="contained" onClick={handleFormSubmit}>
                {formMode === 'create' ? 'Criar' : 'Salvar'}
              </LdsButton>
            </>
          }
        >
          <div className={styles.form}>
            {formError && <LdsAlert color="critical" description={formError} />}
            <LdsFormField label={primaryExceptionType?.label ?? 'ID'} fieldId={`form-key-${guardrail}`}>
              <LdsTextField
                value={formAccessKeyId}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => setFormAccessKeyId(e.target.value)}
                disabled={formMode === 'edit'}
                placeholder={primaryExceptionType?.placeholder ?? ''}
                aria-label={primaryExceptionType?.label ?? 'ID'}
                data-field={`form-key-${guardrail}`}
              />
            </LdsFormField>
            <LdsFormField label="Validade (DD/MM/YYYY)" fieldId={`form-date-${guardrail}`}>
              <LdsTextField
                value={formDate}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => setFormDate(e.target.value)}
                placeholder="DD/MM/YYYY"
                aria-label="Data de validade"
                data-field={`form-date-${guardrail}`}
              />
            </LdsFormField>
          </div>
        </LdsDialog>
      )}

      {/* Delete Confirmation */}
      {deleteDialog.open && (
        <LdsDialog
          open={deleteDialog.open}
          onClose={() => setDeleteDialog({ open: false, accessKeyId: '' })}
          title="Confirmar remoção"
          closeButtonAriaLabel="Fechar"
          footer={
            <>
              <LdsButton variant="outlined" onClick={() => setDeleteDialog({ open: false, accessKeyId: '' })}>
                Cancelar
              </LdsButton>
              <LdsButton variant="contained" color="critical" onClick={handleDeleteConfirm}>
                Remover
              </LdsButton>
            </>
          }
        >
          <LdsTypography variant="bodyMd">
            Remover a exceção <strong>{deleteDialog.accessKeyId}</strong>? Esta ação não pode ser desfeita.
          </LdsTypography>
        </LdsDialog>
      )}
    </div>
  );
}
